import { apiUrl } from './api_config'

const API_BASE_URL = apiUrl('/users')

export const USERS_PAGE_SIZE = 20

async function request(url, options = {}) {
  const response = await fetch(url, {
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
    ...options,
  })

  if (!response.ok) {
    let message = `User request failed (${response.status})`
    try {
      const error = await response.json()
      message = error.message || error.error || message
    } catch {
      // Keep the HTTP status message when the server does not return JSON.
    }
    throw new Error(message)
  }

  return response.status === 204 ? null : response.json()
}

function findList(data, keys) {
  if (Array.isArray(data)) return data
  if (!data || typeof data !== 'object') return []
  for (const key of keys) {
    const list = findList(data[key], keys)
    if (list.length || Array.isArray(data[key])) return list
  }
  return []
}

export async function getUsers({
  page = 1,
  search = '',
  role = '',
  limit = USERS_PAGE_SIZE,
  signal,
} = {}) {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  })
  if (search.trim()) params.set('search', search.trim())
  if (role.trim()) params.set('role', role.trim())
  const data = await request(`${API_BASE_URL}/all?${params}`, { signal })
  const hasNext = Boolean(
    data?.hasNextPage ?? data?.has_next_page ?? data?.hasNext ?? data?.has_next,
  )
  return {
    users: findList(data, ['users', 'results', 'items', 'data']),
    currentPage: Number(data?.page ?? data?.currentPage ?? page),
    nextPage: hasNext ? page + 1 : null,
  }
}

export async function getUserRoles(signal) {
  const data = await request(`${API_BASE_URL}/roles`, { signal })
  return findList(data, ['roles', 'results', 'items', 'data'])
}

export async function createUserRole(roleName) {
  const data = await request(`${API_BASE_URL}/roles`, {
    method: 'POST',
    body: JSON.stringify({ role_name: roleName }),
  })
  return data?.data ?? data
}

export async function getRolePermissions(roleId, signal) {
  return request(
    `${API_BASE_URL}/roles/${encodeURIComponent(roleId)}/permissions`,
    { signal },
  )
}

export async function updateRolePermissions(roleId, permissions) {
  return request(
    `${API_BASE_URL}/roles/${encodeURIComponent(roleId)}/permissions`,
    {
      method: 'PUT',
      body: JSON.stringify({ permissions }),
    },
  )
}

export async function createUser(payload) {
  const data = await request(`${API_BASE_URL}/add`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
  return data?.data ?? data
}

export async function updateUser(userId, payload) {
  const data = await request(
    `${API_BASE_URL}/update/${encodeURIComponent(userId)}`,
    { method: 'PUT', body: JSON.stringify(payload) },
  )
  return data?.data ?? data
}

export async function deleteUser(userId) {
  return request(`${API_BASE_URL}/delete/${encodeURIComponent(userId)}`, {
    method: 'DELETE',
  })
}
